import { useLiveQuery } from "dexie-react-hooks";
import {
  Download,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { cn } from "@repo/core";
import { TransactionModal } from "../components/TransactionModal";
import { SectionTitle } from "../components/ui/AppCard";
import { AppTable } from "../components/ui/AppTable";
import { useCategories } from "../hooks/useBudgetData";
import { useCurrency } from "../hooks/useCurrency";
import { categoryLabel, categoryLabelById, personLabel } from "../i18n/labels";
import { useT } from "../i18n/useT";
import { db } from "../lib/db";
import type { Transaction } from "../lib/schema";

const TYPE_BADGE: Record<string, string> = {
  income:   "bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800",
  expense:  "bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800",
  transfer: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800",
};

const PAGE_SIZE = 25;

function useIsMobileCards(breakpoint = 600) {
  const [mobile, setMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = () => setMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [breakpoint]);
  return mobile;
}

function TxCard({
  tx,
  catLabel,
  catIcon,
  onEdit,
  onDelete,
  format,
}: {
  tx: Transaction;
  catLabel: string;
  catIcon: string;
  onEdit: () => void;
  onDelete: () => void;
  format: (n: number) => string;
}) {
  const { t } = useT();
  return (
    <div className="app-card border border-border p-3 flex gap-3 items-start">
      <div
        className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center text-lg shrink-0"
        aria-hidden
      >
        {catIcon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{catLabel}</p>
            <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{tx.date}</p>
          </div>
          <span
            className={cn(
              "font-semibold text-sm tabular-nums shrink-0",
              tx.type === "income"
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-foreground"
            )}
          >
            {tx.type === "income" ? "+" : "−"}
            {format(tx.amount)}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-2">
          <span className={cn("app-badge text-[10px]", TYPE_BADGE[tx.type])}>
            {t(`type.${tx.type}`)}
          </span>
          {tx.notes && (
            <span className="text-[11px] text-muted-foreground truncate">{tx.notes}</span>
          )}
        </div>
        <div className="flex gap-2 mt-3">
          <button
            type="button"
            onClick={onEdit}
            className="min-h-[44px] min-w-[44px] flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-border bg-background text-xs font-medium hover:bg-accent transition-colors"
            aria-label={t("tx.editAria")}
          >
            <Pencil size={16} /> {t("common.edit")}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="min-h-[44px] min-w-[44px] flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-destructive/30 text-destructive text-xs font-medium hover:bg-destructive/10 transition-colors"
            aria-label={t("tx.deleteAria")}
          >
            <Trash2 size={16} /> {t("common.delete")}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TransactionsPage({
  year,
  person,
  onOpenCsvImport,
}: {
  year: number;
  person: string;
  onOpenCsvImport?: () => void;
}) {
  const { format } = useCurrency();
  const { t } = useT();
  const isMobileCards = useIsMobileCards(600);
  const [search, setSearch] = useState("");
  const [typeFilter, setType] = useState("all");
  const [catFilter, setCat] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTx, setEditingTx] = useState<Transaction | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const categories = useCategories();
  const accounts = useLiveQuery(() => db.accounts.toArray(), []) ?? [];

  const txs =
    useLiveQuery(
      () =>
        db.transactions
          .filter((t) => {
            const matchYear = t.date.startsWith(String(year));
            const matchPerson = person === "All" || t.person === person;
            return matchYear && matchPerson;
          })
          .reverse()
          .sortBy("date"),
      [year, person]
    ) ?? [];

  const filtered = useMemo(() => {
    let result = txs.filter((t) => !t.skipped);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (tx) =>
          tx.notes?.toLowerCase().includes(q) ||
          tx.person.toLowerCase().includes(q) ||
          personLabel(tx.person, t).toLowerCase().includes(q) ||
          tx.date.includes(q) ||
          String(tx.amount).includes(q) ||
          categoryLabelById(tx.categoryId, categories, t).toLowerCase().includes(q)
      );
    }
    if (typeFilter !== "all") result = result.filter((t) => t.type === typeFilter);
    if (catFilter !== "all") result = result.filter((t) => t.categoryId === catFilter);
    if (dateFrom) result = result.filter((t) => t.date >= dateFrom);
    if (dateTo) result = result.filter((t) => t.date <= dateTo);
    const min = parseFloat(amountMin);
    const max = parseFloat(amountMax);
    if (!isNaN(min)) result = result.filter((t) => t.amount >= min);
    if (!isNaN(max)) result = result.filter((t) => t.amount <= max);
    return result;
  }, [txs, search, typeFilter, catFilter, dateFrom, dateTo, amountMin, amountMax, categories, t]);

  // Reset pagination when filters change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search, typeFilter, catFilter, dateFrom, dateTo, amountMin, amountMax, year, person]);

  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  const catName = (id: string) => categoryLabelById(id, categories, t);
  const catIcon = (id: string) => categories.find((c) => c.id === id)?.icon ?? "📦";
  const accName = (id: string) => accounts.find((a) => a.id === id)?.name ?? id;

  const handleOpenAdd = () => {
    setEditingTx(null);
    setIsModalOpen(true);
  };

  const handleStartEdit = (tx: Transaction) => {
    setEditingTx(tx);
    setIsModalOpen(true);
  };

  const handleDelete = async (tx: Transaction) => {
    if (confirm(t("tx.deleteConfirm"))) await db.transactions.delete(tx.id);
  };

  const handleExport = () => {
    const headers = [
      t("tx.col.date"),
      t("tx.col.type"),
      t("tx.col.amount"),
      t("tx.col.category"),
      t("tx.col.account"),
      t("tx.col.person"),
      t("tx.col.notes"),
    ];
    const rows = filtered.map((row) => [
      row.date,
      t(`type.${row.type}`),
      row.amount,
      catName(row.categoryId),
      accName(row.accountId),
      personLabel(row.person, t),
      row.notes ?? "",
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions-${year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { key: "date", label: t("tx.col.date"), className: "w-28 font-mono text-xs text-muted-foreground" },
    {
      key: "type",
      label: t("tx.col.type"),
      render: (row: Transaction) => (
        <span className={cn("app-badge", TYPE_BADGE[row.type])}>{t(`type.${row.type}`)}</span>
      ),
    },
    {
      key: "categoryId",
      label: t("tx.col.category"),
      render: (row: Transaction) => <span className="text-xs">{catName(row.categoryId)}</span>,
    },
    {
      key: "amount",
      label: t("tx.col.amount"),
      align: "right" as const,
      render: (row: Transaction) => (
        <span
          className={cn(
            "font-semibold text-sm tabular-nums",
            row.type === "income"
              ? "text-emerald-600 dark:text-emerald-400"
              : "text-foreground"
          )}
        >
          {row.type === "income" ? "+" : "-"}
          {format(row.amount)}
        </span>
      ),
    },
    {
      key: "person",
      label: t("tx.col.person"),
      render: (row: Transaction) => (
        <span className="text-[10px] text-muted-foreground">{personLabel(row.person, t)}</span>
      ),
    },
    {
      key: "notes",
      label: t("tx.col.notes"),
      render: (row: Transaction) => (
        <span className="text-xs text-muted-foreground truncate max-w-[120px] block">
          {row.notes}
        </span>
      ),
    },
    {
      key: "actions",
      label: "",
      render: (row: Transaction) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => handleStartEdit(row)}
            className="p-2 min-h-[36px] min-w-[36px] rounded hover:bg-accent transition-colors inline-flex items-center justify-center"
            title={t("common.edit")}
            aria-label={t("tx.editAria")}
          >
            <Pencil size={13} className="text-muted-foreground" />
          </button>
          <button
            onClick={() => handleDelete(row)}
            className="p-2 min-h-[36px] min-w-[36px] rounded hover:bg-destructive/10 transition-colors inline-flex items-center justify-center"
            title={t("common.delete")}
            aria-label={t("tx.deleteAria")}
          >
            <Trash2 size={13} className="text-destructive/70" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="p-4 lg:p-6 space-y-4 w-full max-w-full overflow-x-hidden">
      <SectionTitle
        action={
          <div className="flex flex-wrap gap-2">
            {onOpenCsvImport && (
              <button onClick={onOpenCsvImport} className="app-btn-outline min-h-[44px] sm:min-h-0">
                {t("tx.importCsv")}
              </button>
            )}
            <button onClick={handleExport} className="app-btn-outline min-h-[44px] sm:min-h-0">
              <Download size={13} /> {t("tx.csv")}
            </button>
            <button onClick={handleOpenAdd} className="app-btn min-h-[44px] sm:min-h-0">
              <Plus size={13} /> {t("tx.add")}
            </button>
          </div>
        }
      >
        {t("tx.log")}
      </SectionTitle>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[140px] max-w-full sm:max-w-[12rem]">
          <Search
            size={13}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            placeholder={t("tx.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="app-input pl-7 w-full min-h-[44px] sm:min-h-0"
          />
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setType(e.target.value)}
          className="app-input w-full sm:w-28 min-h-[44px] sm:min-h-0"
        >
          <option value="all">{t("tx.allTypes")}</option>
          <option value="income">{t("type.income")}</option>
          <option value="expense">{t("type.expense")}</option>
          <option value="transfer">{t("type.transfer")}</option>
        </select>
        <select
          value={catFilter}
          onChange={(e) => setCat(e.target.value)}
          className="app-input w-full sm:w-36 min-h-[44px] sm:min-h-0"
        >
          <option value="all">{t("tx.allCategories")}</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {categoryLabel(c, t)}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="app-input w-full sm:w-36 min-h-[44px] sm:min-h-0"
          aria-label={t("tx.fromDate")}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="app-input w-full sm:w-36 min-h-[44px] sm:min-h-0"
          aria-label={t("tx.toDate")}
        />
        <input
          type="number"
          min="0"
          step="0.01"
          placeholder={t("tx.minAmount")}
          value={amountMin}
          onChange={(e) => setAmountMin(e.target.value)}
          className="app-input w-full sm:w-24 min-h-[44px] sm:min-h-0"
        />
        <input
          type="number"
          min="0"
          step="0.01"
          placeholder={t("tx.maxAmount")}
          value={amountMax}
          onChange={(e) => setAmountMax(e.target.value)}
          className="app-input w-full sm:w-24 min-h-[44px] sm:min-h-0"
        />
        {(search ||
          typeFilter !== "all" ||
          catFilter !== "all" ||
          dateFrom ||
          dateTo ||
          amountMin ||
          amountMax) && (
          <button
            onClick={() => {
              setSearch("");
              setType("all");
              setCat("all");
              setDateFrom("");
              setDateTo("");
              setAmountMin("");
              setAmountMax("");
            }}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 min-h-[44px] px-2"
          >
            <X size={11} /> {t("tx.clearFilters")}
          </button>
        )}
        <span className="text-xs text-muted-foreground sm:ml-auto w-full sm:w-auto">
          {t("tx.entries", { n: filtered.length })}
        </span>
      </div>

      {isMobileCards ? (
        <div className="space-y-2.5">
          {visible.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-8">
              {t("tx.noMatch")}
            </p>
          )}
          {visible.map((tx) => (
            <TxCard
              key={tx.id}
              tx={tx}
              catLabel={catName(tx.categoryId)}
              catIcon={catIcon(tx.categoryId)}
              format={format}
              onEdit={() => handleStartEdit(tx)}
              onDelete={() => handleDelete(tx)}
            />
          ))}
          {hasMore && (
            <button
              type="button"
              onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
              className="w-full min-h-[44px] app-btn-outline text-sm"
            >
              {t("tx.loadMore", { n: filtered.length - visibleCount })}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <AppTable
            data={visible}
            columns={columns}
            keyProp="id"
            emptyMessage={t("tx.noMatch")}
          />
          {hasMore && (
            <button
              type="button"
              onClick={() => setVisibleCount((n) => n + PAGE_SIZE)}
              className="w-full app-btn-outline text-sm"
            >
              {t("tx.loadMore", { n: filtered.length - visibleCount })}
            </button>
          )}
        </div>
      )}

      <TransactionModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTx(null);
        }}
        initialData={editingTx}
      />
    </div>
  );
}
